// src/components/common/vibe/VoiceToHRFullDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import {
    MessageSquare, RefreshCw, User, Calendar, X, Filter,
    Trash2, ChevronDown, AlertCircle
} from 'lucide-react';
import vibeService from '../../../services/vibe.service';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' }
];

const CATEGORY_OPTIONS = [
    { value: 'fun_idea', label: '🎉 Fun Idea' },
    { value: 'appreciation', label: '💝 Appreciation' },
    { value: 'improvement', label: '💡 Improvement' },
    { value: 'concern', label: '⚠️ Concern' }
];

const VoiceToHRFullDashboard = ({ isOpen, onClose, selectedSubmission, theme }) => {
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [changingStatus, setChangingStatus] = useState(null);
    const [showStatusDropdown, setShowStatusDropdown] = useState(null);
    const [highlightedId, setHighlightedId] = useState(null);
    const highlightedRowRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            fetchSubmissions();
            // Set highlighted submission if provided
            if (selectedSubmission) {
                setHighlightedId(selectedSubmission.id);
                // Clear highlight after 3 seconds
                setTimeout(() => setHighlightedId(null), 3000);
            }
        }
    }, [isOpen, selectedSubmission]);

    // Scroll to highlighted row after submissions are loaded
    useEffect(() => {
        if (highlightedId && highlightedRowRef.current && !loading) {
            setTimeout(() => {
                highlightedRowRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 100); // Small delay to ensure DOM is ready
        }
    }, [highlightedId, loading, filteredSubmissions]);

    useEffect(() => {
        applyFilters();
    }, [submissions, categoryFilter, statusFilter]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            //console.log('🔄 Fetching Voice to HR submissions...');
            const response = await vibeService.getSuggestionsDashboard();
            //console.log('📋 Fetched submissions:', response.data?.length || 0);

            // Sort by most recent first
            const sorted = (response.data || []).sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
            );

            setSubmissions(sorted);
        } catch (error) {
            console.error('❌ Error fetching submissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...submissions];

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(s => s.suggestion_category === categoryFilter);
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(s => s.suggestion_status === statusFilter);
        }

        setFilteredSubmissions(filtered);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchSubmissions();
        setRefreshing(false);
    };

    const handleStatusChange = async (submissionId, newStatus) => {
        setChangingStatus(submissionId);
        setShowStatusDropdown(null);

        try {
            //console.log(`🔄 Changing status for ${submissionId} to ${newStatus}`);
            await vibeService.updateSuggestionStatus(submissionId, newStatus);

            // Update local state
            setSubmissions(prev => prev.map(s =>
                s.id === submissionId
                    ? { ...s, suggestion_status: newStatus }
                    : s
            ));

            //console.log('✅ Status updated successfully');
        } catch (error) {
            console.error('❌ Error updating status:', error);
            alert('Failed to update status. Please try again.');
        } finally {
            setChangingStatus(null);
        }
    };

    const handleClearCompleted = async () => {
        const completedCount = submissions.filter(s => s.suggestion_status === 'completed').length;

        if (completedCount === 0) {
            alert('No completed submissions to clear.');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${completedCount} completed submission(s)? This action cannot be undone.`)) {
            return;
        }

        try {
            //console.log('🗑️ Clearing completed submissions...');
            await vibeService.clearCompletedSuggestions();

            // Remove completed items from local state
            setSubmissions(prev => prev.filter(s => s.suggestion_status !== 'completed'));

            //console.log('✅ Completed submissions cleared');
        } catch (error) {
            console.error('❌ Error clearing completed submissions:', error);
            alert('Failed to clear completed submissions. Please try again.');
        }
    };

    const getRowClassName = (submission) => {
        const baseClass = `border-b transition-all duration-500 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`;

        // Highlight the selected submission
        if (highlightedId === submission.id) {
            return `${baseClass} ${theme === 'dark' ? 'bg-blue-900/30 shadow-lg' : 'bg-blue-100 shadow-lg'} scale-[1.01]`;
        }

        if (submission.suggestion_status === 'completed') {
            return `${baseClass} ${theme === 'dark' ? 'bg-green-900/10' : 'bg-green-50/50'}`;
        } else if (submission.suggestion_status === 'in_progress') {
            return `${baseClass} ${theme === 'dark' ? 'bg-orange-900/10' : 'bg-orange-50/50'}`;
        } else {
            return `${baseClass} ${theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`;
        }
    };

    const getTextClassName = (submission) => {
        const baseClass = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
        return submission.suggestion_status === 'completed' ? `${baseClass} line-through` : baseClass;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className={`relative w-full max-w-6xl max-h-[90vh] rounded-xl shadow-2xl ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                    {/* Header */}
                    <div className={`flex items-center justify-between p-6 border-b ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <div className="flex items-center gap-3">
                            <MessageSquare className={`w-6 h-6 ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                            <div>
                                <h2 className={`text-2xl font-bold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Voice to HR Dashboard
                                </h2>
                                <p className={`text-sm mt-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Manage employee feedback and suggestions
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-lg transition-colors ${
                                theme === 'dark'
                                    ? 'hover:bg-gray-700 text-gray-400'
                                    : 'hover:bg-gray-100 text-gray-600'
                            }`}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Filters and Actions */}
                    <div className={`p-4 border-b flex flex-wrap items-center gap-4 ${
                        theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
                    }`}>
                        {/* Category Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4" />
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className={`px-3 py-2 rounded-lg border text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="all">All Categories</option>
                                {CATEGORY_OPTIONS.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className={`px-3 py-2 rounded-lg border text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="all">All Statuses</option>
                                {STATUS_OPTIONS.map(status => (
                                    <option key={status.value} value={status.value}>{status.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1" />

                        {/* Clear Completed Button */}
                        <button
                            onClick={handleClearCompleted}
                            disabled={!submissions.some(s => s.suggestion_status === 'completed')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                submissions.some(s => s.suggestion_status === 'completed')
                                    ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            }`}
                        >
                            <Trash2 className="w-4 h-4" />
                            Clear Completed
                        </button>

                        {/* Refresh Button */}
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className={`p-2 rounded-lg transition-colors ${
                                theme === 'dark'
                                    ? 'hover:bg-gray-700 text-gray-400'
                                    : 'hover:bg-gray-100 text-gray-600'
                            } ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>

                        {/* Count Badge */}
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                        }`}>
                            {filteredSubmissions.length} of {submissions.length}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <RefreshCw className={`w-8 h-8 animate-spin ${
                                    theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                                }`} />
                            </div>
                        ) : filteredSubmissions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <MessageSquare className={`w-16 h-16 mb-4 ${
                                    theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                                }`} />
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    {submissions.length === 0
                                        ? 'No submissions yet'
                                        : 'No submissions match the current filters'}
                                </p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="sticky top-0 z-10">
                                    <tr className={`${
                                        theme === 'dark' ? 'bg-gray-800 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'
                                    }`}>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Date
                                        </th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Employee
                                        </th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Category
                                        </th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Message
                                        </th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSubmissions.map((submission) => (
                                        <tr
                                            key={submission.id}
                                            ref={highlightedId === submission.id ? highlightedRowRef : null}
                                            className={getRowClassName(submission)}
                                        >
                                            <td className={`px-4 py-3 text-sm ${getTextClassName(submission)}`}>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    <div>
                                                        <div>{new Date(submission.created_at).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric',
                                                            timeZone: 'Asia/Kolkata'
                                                        })}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(submission.created_at).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                timeZone: 'Asia/Kolkata'
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${getTextClassName(submission)}`}>
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4" />
                                                    {submission.is_anonymous ? (
                                                        <span className="italic text-gray-500">Anonymous</span>
                                                    ) : (
                                                        <span>{submission.author_name || 'Unknown'}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${getTextClassName(submission)}`}>
                                                {CATEGORY_OPTIONS.find(c => c.value === submission.suggestion_category)?.label || submission.suggestion_category}
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${getTextClassName(submission)}`}>
                                                <div className="max-w-md">
                                                    {submission.content}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 relative">
                                                <button
                                                    onClick={() => setShowStatusDropdown(
                                                        showStatusDropdown === submission.id ? null : submission.id
                                                    )}
                                                    disabled={changingStatus === submission.id}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                                        STATUS_OPTIONS.find(s => s.value === submission.suggestion_status)?.color
                                                    } ${
                                                        changingStatus === submission.id
                                                            ? 'opacity-50 cursor-not-allowed'
                                                            : 'cursor-pointer hover:opacity-80'
                                                    }`}
                                                >
                                                    {STATUS_OPTIONS.find(s => s.value === submission.suggestion_status)?.label || 'Pending'}
                                                    <ChevronDown className="w-3 h-3" />
                                                </button>

                                                {/* Status Dropdown */}
                                                {showStatusDropdown === submission.id && (
                                                    <div className={`absolute z-20 mt-1 rounded-lg shadow-lg border ${
                                                        theme === 'dark'
                                                            ? 'bg-gray-700 border-gray-600'
                                                            : 'bg-white border-gray-200'
                                                    }`}>
                                                        {STATUS_OPTIONS.map(status => (
                                                            <button
                                                                key={status.value}
                                                                onClick={() => handleStatusChange(submission.id, status.value)}
                                                                className={`block w-full text-left px-4 py-2 text-sm ${
                                                                    submission.suggestion_status === status.value
                                                                        ? theme === 'dark' ? 'bg-gray-600' : 'bg-gray-100'
                                                                        : theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-50'
                                                                } ${
                                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                                } first:rounded-t-lg last:rounded-b-lg`}
                                                            >
                                                                {status.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VoiceToHRFullDashboard;
